import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, HashRouter, Navigate, Route, Routes } from "react-router-dom";

import * as Sentry from "@sentry/browser";
import { BrowserTracing } from "@sentry/tracing";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "i18n";
import "rum";
import * as serviceWorker from "serviceWorker";
import "styles.scss";

import {
  FeatureFlagProvider,
  PersistentCacheContext,
  bootstrapImpersonationFromUrl,
  checkFeatureFlag,
  getEnvironment,
  getEnvironmentString,
  getLocalKlimVar,
  installReconcilerDomGuard,
  setLocalKlimVar,
} from "utils";

import { Admin } from "pages/admin/_app";
import { App } from "pages/app/_app";
import { MaintenancePage } from "pages/shared/maintenance/MaintenancePage";

// Dev server is localhost; API CORS/cookies expect *.klim.local. Keep the path. Built bundles stay put.
const getDevBounceTarget = () => {
  if (process.env.NODE_ENV !== "development" || window.location.host !== "localhost:3000") {
    return null;
  }

  try {
    const { origin } = new URL(process.env.REACT_APP_FARM_URL);
    // Same origin would loop forever.
    if (origin === window.location.origin) {
      return null;
    }
    const { pathname, search, hash } = window.location;
    return `${origin}${pathname}${search}${hash}`;
  } catch {
    // Bad/missing REACT_APP_FARM_URL: stay on localhost.
    return null;
  }
};

const devBounceTarget = getDevBounceTarget();

if (devBounceTarget) {
  window.location.replace(devBounceTarget);
}

// Must run before the first React commit, so no tool can rewrite the DOM ahead of the guard
installReconcilerDomGuard();

const env = getEnvironment();

if (!env.local) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY,
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
    environment: getEnvironmentString(),
    ignoreErrors: ["n.a.forwardRef is not a function", "ChunkLoadError", "ResizeObserver loop limit exceeded", "Can't find variable: $"],
    denyUrls: [
      "farms.klim.local:3000",
      "admin.klim.local:3000",
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
  });
}

const getRoutes = () => {
  if ((env.staging || env.development) && !getLocalKlimVar("hasStagingAccess")) {
    if (window.location.search.replace(/\/$/, "") === `?verysecretpassword=${process.env.REACT_APP_STAGING_PASSWORD}`) {
      setLocalKlimVar("hasStagingAccess", true);
      // eslint-disable-next-line
    } else if (prompt("Please enter password", "") === process.env.REACT_APP_STAGING_PASSWORD) {
      setLocalKlimVar("hasStagingAccess", true);
    } else {
      return <></>;
    }
  }

  // TODO: Remove after infra migration
  // Check maintenance mode
  const isMaintenanceModeEnabled = false; // Change this to true to enable the maintenance mode
  const hasBypassParam = env.production ? checkFeatureFlag("bypass-maintenance") : true;

  if (isMaintenanceModeEnabled && !hasBypassParam) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/*" element={<MaintenancePage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (env.app) {
    return (
      <HashRouter>
        <Routes>
          <Route path="/*" element={<App />} />
          <Route path="/" element={<Navigate replace to="/sign-in" />} />
        </Routes>
      </HashRouter>
    );
  }

  if (env.admin) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<Admin />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/*" element={<App />} />
        <Route path="/" element={<Navigate replace to="/sign-in" />} />
      </Routes>
    </BrowserRouter>
  );
};

const root = createRoot(document.getElementById("root"));

// Skip render while the bounce is in flight.
if (!devBounceTarget) {
  if (!env.admin) bootstrapImpersonationFromUrl();

  root.render(
    <PersistentCacheContext>
      <FeatureFlagProvider>
        <Suspense fallback={<></>}>{getRoutes()}</Suspense>
        {/* lifts the devtools toggle above the Iubenda cookie-preferences button, which shares the same corner */}
        <style>{".tsqd-open-btn-container { margin-bottom: 3rem; }"}</style>
        <ReactQueryDevtools initialIsOpen={false} />
      </FeatureFlagProvider>
    </PersistentCacheContext>
  );
}

serviceWorker.unregister();
